import React, { useState } from 'react';
import { useNotes } from '../../context/NotesContext';
import useScreenSize from '../../hooks/useScreenSize';
import '../../styles/glass-morphism.css';

function CategoryList() {
  const { categories, selectedCategory, selectCategory, createCategory, deleteCategory } = useNotes();
  const { isMobile } = useScreenSize();
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryColor, setNewCategoryColor] = useState('#0778ad');
  const [hoveredCategory, setHoveredCategory] = useState(null);

  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCategoryName.trim()) return;

    const result = await createCategory({
      name: newCategoryName.trim(),
      color: newCategoryColor,
    });

    if (result.success) {
      setNewCategoryName('');
      setNewCategoryColor('#0778ad');
      setShowAddForm(false);
    }
  };

  const handleDeleteCategory = async (categoryId, e) => {
    e.stopPropagation();
    if (window.confirm('Delete this category? Notes in this category will not be deleted.')) {
      await deleteCategory(categoryId);
    }
  };

  const handleAddButtonClick = () => {
    console.log('Add button clicked, current showAddForm:', showAddForm);
    setShowAddForm(!showAddForm);
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3 style={styles.title}>Categories</h3>
        <button
          type="button"
          onClick={handleAddButtonClick}
          className="internal-primary-action"
          style={{
            ...styles.addButton,
            width: isMobile ? '44px' : '32px',
            height: isMobile ? '44px' : '32px',
          }}
        >
          +
        </button>
      </div>

      {showAddForm && (
        <form onSubmit={handleAddCategory} style={styles.addForm}>
          <input
            type="text"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            placeholder="Category name"
            style={styles.input}
            autoFocus
          />
          <input
            type="color"
            value={newCategoryColor}
            onChange={(e) => setNewCategoryColor(e.target.value)}
            style={styles.colorInput}
          />
          <div style={styles.formButtons}>
            <button type="submit" className="success-action" style={styles.saveButton}>
              Add
            </button>
            <button
              type="button"
              onClick={() => {
                setShowAddForm(false);
                setNewCategoryName('');
              }}
              style={styles.cancelButton}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div
        onClick={() => selectCategory(null)}
        style={{
          ...styles.categoryItem,
          padding: isMobile ? '14px' : '12px',
          minHeight: isMobile ? '44px' : 'auto',
          ...(selectedCategory === null ? styles.categoryItemActive : {}),
        }}
        className="glass-hover smooth-transition"
      >
        <div style={{ ...styles.colorDot, background: '#999' }} />
        <span style={styles.categoryName}>All Notes</span>
      </div>

      {categories.map((category) => (
        <div
          key={category.id}
          onClick={() => selectCategory(category)}
          onMouseEnter={() => setHoveredCategory(category.id)}
          onMouseLeave={() => setHoveredCategory(null)}
          style={{
            ...styles.categoryItem,
            padding: isMobile ? '14px' : '12px',
            minHeight: isMobile ? '44px' : 'auto',
            ...(selectedCategory && selectedCategory.id === category.id ? styles.categoryItemActive : {}),
          }}
          className="glass-hover smooth-transition"
        >
          <div style={{ ...styles.colorDot, background: category.color }} />
          <span style={styles.categoryName}>{category.name}</span>
          <span style={styles.noteCount}>({category.note_count})</span>
          <button
            type="button"
            onClick={(e) => handleDeleteCategory(category.id, e)}
            className="category-delete-action icon-action"
            style={{
              ...styles.deleteButton,
              minWidth: isMobile ? '40px' : 'auto',
              minHeight: isMobile ? '40px' : 'auto',
              opacity: isMobile ? 1 : (hoveredCategory === category.id ? 1 : 0),
            }}
            title="Delete category"
            aria-label={`Delete ${category.name} category`}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '16px',
    height: '100%',
    overflowY: 'auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  title: {
    color: '#102a43',
    fontSize: '1.2rem',
    fontWeight: '600',
    margin: 0,
  },
  addButton: {
    background: '#0778ad',
    border: '1px solid #0778ad',
    color: 'white',
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    fontSize: '1.5rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  addForm: {
    background: '#eef9fd',
    border: '1px solid #d9e9f2',
    borderRadius: '8px',
    padding: '12px',
    marginBottom: '8px',
  },
  input: {
    width: '100%',
    padding: '8px',
    border: '1px solid #a9c7d7',
    borderRadius: '6px',
    background: 'rgba(255, 255, 255, 0.9)',
    marginBottom: '8px',
    fontSize: '14px',
  },
  colorInput: {
    width: '100%',
    height: '32px',
    border: '1px solid #a9c7d7',
    borderRadius: '6px',
    marginBottom: '8px',
    cursor: 'pointer',
  },
  formButtons: {
    display: 'flex',
    gap: '8px',
  },
  saveButton: {
    flex: 1,
    padding: '6px',
    background: '#067647',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
  },
  cancelButton: {
    flex: 1,
    padding: '6px',
    background: 'white',
    color: '#334e68',
    border: '1px solid #b9d4e2',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  categoryItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px',
    borderRadius: '8px',
    cursor: 'pointer',
    background: '#f8fcfe',
    border: '1px solid #e1edf3',
    position: 'relative',
  },
  categoryItemActive: {
    background: '#e4f6fd',
    border: '1px solid #79cfe9',
  },
  colorDot: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  categoryName: {
    color: '#102a43',
    fontSize: '14px',
    fontWeight: '500',
    flex: 1,
  },
  noteCount: {
    color: '#627d98',
    fontSize: '12px',
  },
  deleteButton: {
    background: '#fef3f2',
    border: '1px solid #f3b7b2',
    color: '#9b2c2c',
    fontSize: '20px',
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: '6px',
    lineHeight: '1',
    opacity: 0,
    transition: 'opacity 0.2s',
  },
  deleteButtonVisible: {
    opacity: 1,
  },
};

export default CategoryList;
